// ===========================================
// View Teacher Dialog Component
// ===========================================

import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  Button,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Assignment as AssignmentIcon,
  BeachAccess as TimeOffIcon,
  CheckCircleOutline as CheckCircleIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  MusicNote as MusicNoteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  School as SchoolIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Teacher } from '../../api/users.api';
import { useLessonsByTeacher } from '../../hooks/useLessons';
import { useTeacherAvailability, useTeacherReplacementInvolvement } from '../../hooks/useUsers';
import { useTeacherUnavailability } from '../../hooks/useTeacherUnavailability';
import { TIME_OFF_STATUS_CHIP, timeOffRangeLabel, timeOffTermWeekLabels } from '../../utils/timeOff';
import { useTerms } from '../../hooks/useAdmin';
import type { ReplacementInvolvementLesson } from '../../api/users.api';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useTeacherPendingNotes } from '../../hooks/useNotes';
import { useSchoolInfo } from '../../hooks/useAdminSettings';
import AccessChip from '../common/AccessChip';
import SendLoginLinkButton from './SendLoginLinkButton';
import { getContrastTextColor } from '../../styles/brandColors';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

interface Props {
  open: boolean;
  teacher: Teacher | null;
  onClose: () => void;
  onEdit: () => void;
  /**
   * Show Send login link (#589). The PAGE decides: `people_teachers.write`, and for an ADMIN or
   * staff-role login the owner only. Defaults OFF.
   */
  allowSendLoginLink?: boolean;
}

/**
 * Dialog showing teacher profile details (read-only view)
 */
export default function ViewTeacherDialog({ open, teacher, onClose, onEdit, allowSendLoginLink = false }: Props) {
  const { data: lessons, isLoading: isLoadingLessons } = useLessonsByTeacher(teacher?.id || '');
  const { data: availability, isLoading: isLoadingAvailability } = useTeacherAvailability(teacher?.id || null);
  const { data: pendingNotes, isLoading: isLoadingPending } = useTeacherPendingNotes(teacher?.id || '');
  const { data: involvement, isLoading: isLoadingInvolvement } = useTeacherReplacementInvolvement(teacher?.id);
  const { data: timeOffPeriods, isLoading: isLoadingTimeOff } = useTeacherUnavailability(teacher?.id || null);
  // Terms let each time-off row show which term week(s) the range lands on.
  const { data: terms } = useTerms();
  const { data: schoolInfo } = useSchoolInfo();

  if (!teacher) return null;

  const activeLessons = lessons?.filter((l) => l.isActive) || [];
  const activeSlots = availability?.filter((s) => s.isActive) || [];

  // Group availability by day
  const availabilityByDay = activeSlots.reduce<Record<number, { startTime: string; endTime: string }[]>>(
    (acc, slot) => {
      if (!acc[slot.dayOfWeek]) acc[slot.dayOfWeek] = [];
      acc[slot.dayOfWeek].push({ startTime: slot.startTime, endTime: slot.endTime });
      return acc;
    },
    {}
  );

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours, 10);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  const hasPendingTasks = pendingNotes && pendingNotes.totalPending > 0;

  // Time off: current/upcoming PENDING + APPROVED periods shown in full;
  // finished APPROVED periods collapse to a count (DECLINED/CANCELLED hidden —
  // they never affected availability). Dates are @db.Date UTC-midnight ISO
  // strings, so a lexicographic compare against today's key is exact.
  const todayKey = new Date().toISOString().slice(0, 10);
  const liveTimeOff = (timeOffPeriods ?? []).filter(
    (p) => (p.status === 'PENDING' || p.status === 'APPROVED') && p.endDate.slice(0, 10) >= todayKey
  );
  const pastApprovedTimeOffCount = (timeOffPeriods ?? []).filter(
    (p) => p.status === 'APPROVED' && p.endDate.slice(0, 10) < todayKey
  ).length;

  // "Wks 2, 3 (12 May, 19 May)" / "Wk 5 (12 May)" for an involvement row.
  const formatInvolvementWeeks = (row: ReplacementInvolvementLesson): string => {
    const weekPart = row.weeks.length === 1 ? `Wk ${row.weeks[0]}` : `Wks ${row.weeks.join(', ')}`;
    const datePart = row.dates
      .map((d) => new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }))
      .join(', ');
    return `${weekPart} (${datePart})`;
  };

  const beingReplaced = involvement?.beingReplaced ?? [];
  const covering = involvement?.covering ?? [];
  const hasInvolvement = beingReplaced.length > 0 || covering.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {teacher.color && (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: teacher.color,
                  borderRadius: 0.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  flexShrink: 0,
                }}
              />
            )}
            <Typography variant="h6">
              {teacher.User.firstName} {teacher.User.lastName}
            </Typography>
            <Chip
              label={teacher.isActive ? 'Active' : 'Inactive'}
              size="small"
              color={teacher.isActive ? 'success' : 'default'}
            />
            <AccessChip role={teacher.User.role} staffRole={teacher.staffRole} />
          </Box>
          {schoolInfo?.requireWorkingWithChildrenId && teacher.workingWithChildrenId && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 0 }}>
              WWCC ID: {teacher.workingWithChildrenId}
            </Typography>
          )}
        </Box>
        <Box>
          <Tooltip title="Edit Teacher">
            <IconButton size="small" onClick={onEdit}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Basic Information */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              <PersonIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Basic Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography>
                    <Link
                      href={`mailto:${teacher.User.email}`}
                      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <EmailIcon sx={{ fontSize: 16 }} />
                      {teacher.User.email}
                    </Link>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography>
                    {teacher.User.phone ? (
                      <Link
                        href={`tel:${teacher.User.phone}`}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <PhoneIcon sx={{ fontSize: 16 }} />
                        {teacher.User.phone}
                      </Link>
                    ) : (
                      'Not set'
                    )}
                  </Typography>
                </Grid>
                {teacher.bio && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Bio
                    </Typography>
                    <Typography variant="body2">{teacher.bio}</Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Instruments
                  </Typography>
                  {teacher.TeacherInstrument && teacher.TeacherInstrument.length > 0 ? (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                      {teacher.TeacherInstrument.map((ti) => {
                        const instrumentColor = ti.Instrument?.color;
                        return ti.Instrument ? (
                          <Chip
                            key={ti.id}
                            label={ti.Instrument.name}
                            size="small"
                            sx={
                              instrumentColor
                                ? {
                                    bgcolor: instrumentColor,
                                    color: getContrastTextColor(instrumentColor),
                                  }
                                : undefined
                            }
                            color={!instrumentColor ? 'default' : undefined}
                          />
                        ) : null;
                      })}
                    </Stack>
                  ) : (
                    <Typography color="text.secondary">No instruments assigned</Typography>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </Box>

          {/* Teacher Tasks */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              <AssignmentIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Teacher Tasks
            </Typography>
            {isLoadingPending ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : hasPendingTasks ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: '#FFF5F3',
                  borderColor: '#FFAE9E',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <WarningIcon sx={{ fontSize: 20, color: '#FFAE9E' }} />
                  <Typography variant="body2" fontWeight="medium" color="error.dark">
                    {pendingNotes!.totalPending} pending task{pendingNotes!.totalPending !== 1 ? 's' : ''}
                  </Typography>
                </Box>
                <Stack spacing={0.5} sx={{ ml: 3.5 }}>
                  {pendingNotes!.pendingClassNotes > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {pendingNotes!.pendingClassNotes} pending class note{pendingNotes!.pendingClassNotes !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  {pendingNotes!.pendingStudentNotes > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {pendingNotes!.pendingStudentNotes} pending student note{pendingNotes!.pendingStudentNotes !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: '#F0FFF4',
                  borderColor: 'success.light',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                  <Typography variant="body2" color="success.dark" fontWeight="medium">
                    All caught up
                  </Typography>
                </Box>
              </Paper>
            )}
          </Box>

          {/* Availability */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Availability
            </Typography>
            {isLoadingAvailability ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : Object.keys(availabilityByDay).length > 0 ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={0.5}>
                  {Object.entries(availabilityByDay)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([day, slots]) => (
                      <Box key={day} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ minWidth: 90, fontWeight: 500 }}>
                          {DAYS_OF_WEEK[Number(day)]}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {slots.map((s) => `${formatTime(s.startTime)} - ${formatTime(s.endTime)}`).join(', ')}
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography color="text.secondary">No availability set</Typography>
              </Paper>
            )}
          </Box>

          {/* Time Off / Unavailability (current + upcoming; approved blocks bookings) */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              <TimeOffIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Time Off / Unavailability
            </Typography>
            {isLoadingTimeOff ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : liveTimeOff.length > 0 ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  {liveTimeOff.map((period) => {
                    const chip = TIME_OFF_STATUS_CHIP[period.status];
                    return (
                      <Box key={period.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {timeOffRangeLabel(period)}
                        </Typography>
                        <Chip
                          label={chip.label}
                          size="small"
                          sx={{ bgcolor: chip.bgcolor, color: chip.color, fontWeight: 600 }}
                        />
                        {timeOffTermWeekLabels(period, terms).map((label) => (
                          <Chip key={label} label={label} size="small" variant="outlined" />
                        ))}
                        {period.reason && (
                          <Typography variant="caption" color="text.secondary">
                            · {period.reason}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                  {pastApprovedTimeOffCount > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      + {pastApprovedTimeOffCount} past period{pastApprovedTimeOffCount !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            ) : (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography color="text.secondary">
                  No upcoming time off
                  {pastApprovedTimeOffCount > 0
                    ? ` (${pastApprovedTimeOffCount} past period${pastApprovedTimeOffCount !== 1 ? 's' : ''})`
                    : ''}
                </Typography>
              </Paper>
            )}
          </Box>

          {/* Classes */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              <SchoolIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Classes ({activeLessons.length})
            </Typography>
            {isLoadingLessons ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : activeLessons.length > 0 ? (
              <Stack spacing={1}>
                {activeLessons.map((lesson) => (
                  <Paper key={lesson.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <MusicNoteIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                        <Typography variant="body2" fontWeight="medium">
                          {lesson.name}
                        </Typography>
                        <Chip
                          label={lesson.LessonType.name}
                          size="small"
                          sx={
                            lesson.LessonType.color
                              ? {
                                  bgcolor: lesson.LessonType.color,
                                  color: getContrastTextColor(lesson.LessonType.color),
                                }
                              : undefined
                          }
                          color={!lesson.LessonType.color ? 'default' : undefined}
                        />
                        {lesson.Instrument && (
                          <Chip
                            label={lesson.Instrument.name}
                            size="small"
                            variant="outlined"
                            sx={
                              lesson.Instrument.color
                                ? {
                                    borderColor: lesson.Instrument.color,
                                    color: lesson.Instrument.color,
                                  }
                                : undefined
                            }
                          />
                        )}
                      </Box>
                      <Chip
                        label={`${lesson._count.LessonEnrollment} student${lesson._count.LessonEnrollment !== 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', ml: 3.5 }}>
                      {lesson.dayOfWeek != null ? `${DAYS_OF_WEEK[lesson.dayOfWeek]} ${formatTime(lesson.startTime!)} - ${formatTime(lesson.endTime!)}` : 'Schedule TBD'}
                      {' · '}
                      {lesson.Room.Location.name} — {lesson.Room.name}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            ) : (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography color="text.secondary">No active classes assigned</Typography>
              </Paper>
            )}
          </Box>

          {/* Replacement involvement — per-week, one row per lesson */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
              <SwapHorizIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
              Replacement Cover
            </Typography>
            {isLoadingInvolvement ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            ) : hasInvolvement ? (
              <Stack spacing={1.5}>
                {/* Lessons where this teacher is being replaced */}
                {beingReplaced.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Being replaced ({beingReplaced.length})
                    </Typography>
                    <Stack spacing={1}>
                      {beingReplaced.map((row) => (
                        <Paper key={row.lessonId} variant="outlined" sx={{ p: 1.5, borderLeft: '3px solid #FFAE9E' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight="medium">{row.lessonName}</Typography>
                            <Typography variant="caption" color="text.secondary">· {formatInvolvementWeeks(row)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              · Covered by{' '}
                              {row.otherTeacher
                                ? `${row.otherTeacher.firstName} ${row.otherTeacher.lastName}`
                                : 'regular teacher'}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Lessons where this teacher is the replacement */}
                {covering.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      Covering for others ({covering.length})
                    </Typography>
                    <Stack spacing={1}>
                      {covering.map((row) => (
                        <Paper key={row.lessonId} variant="outlined" sx={{ p: 1.5, borderLeft: '3px solid #96DAC9' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="body2" fontWeight="medium">{row.lessonName}</Typography>
                            <Typography variant="caption" color="text.secondary">· {formatInvolvementWeeks(row)}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              · Replacing{' '}
                              {row.otherTeacher
                                ? `${row.otherTeacher.firstName} ${row.otherTeacher.lastName}`
                                : 'regular teacher'}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            ) : (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography color="text.secondary">No replacement cover</Typography>
              </Paper>
            )}
          </Box>

        </Box>
      </DialogContent>

      {/* Three controls do not fit a phone's width on one row (/qa Phase 5 L4). */}
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {allowSendLoginLink && (
          <Box sx={{ mr: 'auto', pl: 1 }}>
            <SendLoginLinkButton
              kind="teacher"
              id={teacher.id}
              name={`${teacher.User.firstName} ${teacher.User.lastName}`}
              disabledReason={
                teacher.isActive && teacher.User.isActive !== false
                  ? undefined
                  : 'This teacher is inactive, so a link would not work.'
              }
            />
          </Box>
        )}
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" onClick={onEdit}>
          Edit Teacher
        </Button>
      </DialogActions>
    </Dialog>
  );
}
